import React, { useState, useEffect } from 'react';
import { adminServices } from './services/adminServices';
import { translationService } from './services/translationService';

const QUESTION_TYPES_WITH_ESTIMATE = new Set([
  'currency_value',
  'value',
  'months',
  'percentage',
  'ratio',
  'count_toggle',
  'percentage_outcome'
]);

const Questionnaire = ({ schoolId, langKey, activeLang, schoolProfile, onComplete }) => {
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const lastUpdate = schoolProfile?.lastForensicUpdate?.toDate();
  const isLocked = lastUpdate && lastUpdate.getFullYear() === new Date().getFullYear();
  const [translationCache, setTranslationCache] = useState(null);

  const schema = {
    introTitle: "IFIP Global Inclusion Index",
    introBody: "Thank you for completing your Institutional Self-Evaluation. To further strengthen your evidence base, we invite you to participate in the IFIP Global Inclusion Index. This module captures forensic, quantitative metrics that provide a 'data-layer' to your narratives. Participation is entirely voluntary. You may omit any questions, select 'Not Applicable', or leave the field blank if you don’t know. Whenever you enter a number, ratio, or percentage that is not from an exact audited source, tick \"This figure is an estimate\" under that answer so we can interpret it correctly. A careful, informed figure is still very valuable for global trends—even when it is not precise.",
    estimateCheckboxLabel: "This figure is an estimate (not from exact audited records)",
    btnSubmit: "Submit & Finalize Certification",

    categories: [
      {
        id: "cat1",
        title: "Category 1: Financial & Resource Metrics",
        questions: [
          {
            id: "q1_1",
            label: "Total Inclusion Spend: Total annual expenditure specifically allocated to SEN/Inclusion (Staff, resources, external fees).",
            type: "currency_value",
            placeholderCurrency: "e.g. USD",
            placeholderAmount: "Annual total (numbers only)"
          },
          {
            id: "q1_2",
            label: "Capital & Environment Investment: Total spend on physical accessibility modifications or specialist furnishings over the last 36 months.",
            type: "value",
            placeholder: "Total spend, last 36 months (numbers)"
          },
          {
            id: "q1_3",
            label: "The 'Shadow Workforce': Are there support staff funded directly by parents (e.g. private 1:1 tutors/therapists)?",
            type: "count_toggle",
            placeholderCount: "If Yes: approximate headcount"
          }
        ]
      },
      {
        id: "cat2",
        title: "Category 2: Technology & Digital Equity",
        questions: [
          {
            id: "q2_1",
            label: "The 'Accessibility Gap': Total number of students requiring specialized tech to access curriculum who do not have it.",
            type: "value",
            placeholder: "Number of students (whole number)"
          },
          {
            id: "q2_2",
            label: "Technology Type Urgency: Which categories are most required?",
            type: "checklist",
            options: ["AAC devices", "Screen readers", "Physical interface adaptors", "Acoustic enhancement", "AI-driven literacy tools"]
          },
          {
            id: "q2_3",
            label: "Digital Audit: Does your school’s LMS/VLE undergo regular accessibility audits (e.g. WCAG 2.1+)?",
            type: "boolean_na",
            placeholder: "Yes, No, or N/A"
          }
        ]
      },
      {
        id: "cat3",
        title: "Category 3: Governance, Leadership & Reporting",
        questions: [
          {
            id: "q3_1",
            label: "Specialist Retention: How many months has the current Inclusion Lead/SENCo been in their post?",
            type: "months",
            placeholder: "Months in current role (number)"
          },
          {
            id: "q3_2",
            label: "Governance & Strategy: Is 'Inclusion Progress' explicitly featured in Formal Governance Reports in the last 12 months?",
            type: "boolean_na",
            placeholder: "Yes, No, or N/A"
          },
          {
            id: "q3_3",
            label: "Parental Engagement: Number of formalized, minuted consultation meetings held specifically for parents of students with additional needs per year?",
            type: "value",
            placeholder: "Meetings per year (number)"
          },
          {
            id: "q3_4",
            label: "Complaint Density: Approx. percentage of formal parental complaints relating specifically to inclusion provision?",
            type: "percentage",
            placeholder: "Approx. % (e.g. 5)"
          }
        ]
      },
      {
        id: "cat4",
        title: "Category 4: Behaviour, Impact & Transition",
        questions: [
          {
            id: "q4_1",
            label: "Successful Transition: % of students requiring support who successfully transitioned onwards from school?",
            type: "percentage_outcome",
            outcomes: ["Employment", "Further Education", "Vocational Training"],
            placeholderPercent: "% succeeding (e.g. 72)"
          },
          {
            id: "q4_2",
            label: "Exclusion Disparity: Ratio of suspensions for students with additional needs vs general population (e.g. 1:1 or 2:1)?",
            type: "ratio",
            placeholder: "Ratio, e.g. 2:1 or 1:1"
          },
          {
            id: "q4_3",
            label: "Behavioural Triggers: % of 'Serious Incidents' where sensory overload/unmet need was a primary trigger?",
            type: "percentage",
            placeholder: "Approx. % (e.g. 30)"
          }
        ]
      },
      {
        id: "cat5",
        title: "Category 5: Community Ecosystem",
        questions: [
          {
            id: "q5_1",
            label: "Partnership Density: Number of active, signed MOUs/SLAs with external health, social, or NGO partners?",
            type: "value",
            placeholder: "Active signed agreements (count)"
          },
          {
            id: "q5_2",
            label: "The 'Magnet Effect': Ratio of 'Inclusion-Specific' applications received vs actual places available (e.g. 5:1)?",
            type: "ratio",
            placeholder: "Apps to places, e.g. 5:1"
          }
        ]
      }
    ]
  };

  useEffect(() => {
    if (langKey === 'en') {
      setTranslationCache(null);
      return;
    }

    const loadQuestionnaireTranslation = async () => {
      try {
        const translatedSchema = await translationService.getTranslation(langKey, 'forensic_schema', schema);
        setTranslationCache(translatedSchema);
      } catch (e) {
        console.error("Questionnaire Translation Error", e);
      }
    };

    loadQuestionnaireTranslation();
  }, [langKey]);

  const activeContent = translationCache || schema;

  const handleUpdate = (id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const mergeForm = (patch) => {
    setFormData(prev => ({ ...prev, ...patch }));
  };

  const renderEstimateCheckbox = (q) => {
    if (!QUESTION_TYPES_WITH_ESTIMATE.has(q.type)) return null;
    if (q.type === 'count_toggle' && formData[q.id] === 'No') return null;
    const estKey = `${q.id}_estimate`;
    const labelText = activeContent.estimateCheckboxLabel || schema.estimateCheckboxLabel;
    return (
      <label className="questionnaire-estimate-option">
        <input
          type="checkbox"
          checked={Boolean(formData[estKey])}
          onChange={e => handleUpdate(estKey, e.target.checked)}
        />
        <span>{labelText}</span>
      </label>
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await adminServices.saveQuestionnaire(schoolId, formData);
      setShowSuccess(true);
    } catch (err) {
      const prefix = activeLang?.ui?.questionnaire_submission_error || "Submission error";
      alert(`${prefix}: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const placeholderFallback = (q) => {
    const byType = {
      value: 'Number or short answer',
      months: 'Months (number)',
      percentage: 'Approx. % (e.g. 10)',
      ratio: 'Ratio (e.g. 2:1)',
      boolean_na: 'Yes, No, or N/A'
    };
    return q.placeholder || byType[q.type] || 'Your answer';
  };

  const renderInput = (q) => {
    switch (q.type) {
      case 'currency_value':
        return (
          <div className="questionnaire-field-stack">
            <div className="questionnaire-input-row">
              <input
                className="questionnaire-input questionnaire-input--narrow"
                title={q.placeholderCurrency || 'ISO currency, e.g. USD'}
                placeholder={q.placeholderCurrency || 'e.g. USD'}
                onChange={e => handleUpdate(`${q.id}_curr`, e.target.value)}
              />
              <input
                className="questionnaire-input"
                placeholder={q.placeholderAmount || 'Annual total'}
                onChange={e => handleUpdate(`${q.id}_val`, e.target.value)}
              />
            </div>
            {renderEstimateCheckbox(q)}
          </div>
        );
      case 'count_toggle':
        return (
          <div className="questionnaire-field-stack">
            <div className="questionnaire-binary">
              <button
                type="button"
                className={`questionnaire-binary-btn${formData[q.id] === 'No' ? ' questionnaire-binary-btn--active' : ''}`}
                onClick={() => mergeForm({ [q.id]: 'No', [`${q.id}_estimate`]: false })}
              >
                No
              </button>
              <input
                className="questionnaire-input questionnaire-input--grow"
                placeholder={q.placeholderCount || 'If Yes: approximate number'}
                onChange={e => handleUpdate(q.id, e.target.value)}
              />
            </div>
            {renderEstimateCheckbox(q)}
          </div>
        );
      case 'checklist':
        return (
          <div className="questionnaire-checklist">
            {q.options.map(opt => (
              <label key={opt} className="questionnaire-checklist-item">
                <input
                  type="checkbox"
                  onChange={e => {
                    const current = formData[q.id] || [];
                    handleUpdate(q.id, e.target.checked ? [...current, opt] : current.filter(o => o !== opt));
                  }}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        );
      case 'percentage_outcome':
        return (
          <div className="questionnaire-field-stack">
            <div>
              <input
                className="questionnaire-input questionnaire-input--stack"
                placeholder={q.placeholderPercent || '% (e.g. 72)'}
                onChange={e => handleUpdate(q.id, e.target.value)}
              />
              <div className="questionnaire-outcome-row">
                {q.outcomes.map(o => (
                  <label key={o} className="questionnaire-outcome-option">
                    <input type="radio" name={q.id} onChange={() => handleUpdate(`${q.id}_outcome`, o)} />
                    <span>{o}</span>
                  </label>
                ))}
              </div>
            </div>
            {renderEstimateCheckbox(q)}
          </div>
        );
      default:
        return (
          <div className="questionnaire-field-stack">
            <input
              className="questionnaire-input"
              placeholder={placeholderFallback(q)}
              onChange={e => handleUpdate(q.id, e.target.value)}
            />
            {renderEstimateCheckbox(q)}
          </div>
        );
    }
  };

  return (
    <div className="questionnaire-container">
      {(showSuccess || isLocked) ? (
        <div className="questionnaire-modal-overlay">
          <div className="questionnaire-modal-card">
            <div className="questionnaire-modal-emoji" aria-hidden>🏆</div>
            <h2 className="questionnaire-modal-title">{activeLang.accreditationForm.reg_ui.success_quest}</h2>
            <p className="questionnaire-modal-body">
              {new Date().getFullYear()} {activeLang.ui.t3_status_synced || "Data Synced"}
            </p>
            <button type="button" className="questionnaire-modal-cta" onClick={onComplete}>
              {activeLang.ui.btn_back_dashboard || "Dashboard"}
            </button>
          </div>
        </div>
      ) : (
        <div className="questionnaire-inner">
          <header className="questionnaire-header">
            <img src="/images/01%20IFIP%20Logo.png" className="questionnaire-logo" alt="IFIP" />
            <h1 className="questionnaire-intro-title">{activeContent.introTitle}</h1>
            <p className="questionnaire-intro-body">{activeContent.introBody}</p>
          </header>

          {activeContent.categories.map(cat => (
            <section key={cat.id} className="questionnaire-category">
              <h2 className="questionnaire-category-title">{cat.title}</h2>
              <div className="questionnaire-questions">
                {cat.questions.map(q => (
                  <div key={q.id} className="questionnaire-question">
                    <label className="questionnaire-question-label">{q.label}</label>
                    {renderInput(q)}
                  </div>
                ))}
              </div>
            </section>
          ))}

          <button
            type="button"
            className="questionnaire-submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Syncing Forensic Data..." : activeContent.btnSubmit}
          </button>
        </div>
      )}
    </div>
  );
};

export default Questionnaire;
